import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import RuleCard from './RuleCard';
import useRuleValidation from './useRuleValidation';
import { newRuleId } from './serialization';
import { flattenFields } from './paths';

/**
 * Top-level orchestrator for x-allOf conditional rule authoring.
 *
 * Data model (see serialization.js):
 *   Rule = { id, negated, ifConditions[], thenFields[], elseFields[], raw? }
 *
 * Validation runs debounced via useRuleValidation. Errors propagate up via
 * `onValidationChange` so the parent save flow can block on them.
 */
export default function XAllOfBuilder({
	fields,
	rules,
	onRulesChange,
	onValidationChange,
	isBuildex,
}) {
	// Phase 1b: surface every addressable path (top-level AND nested) so
	// rules can target fields like `address.country` or
	// `manifest[].consignee.email`. paths.js handles Deleted filtering.
	//
	// Bug #4: memoize on the `fields` reference. Without this, every parent
	// re-render (autosave PATCH, sibling state change) rebuilds the option
	// array with new object identities, and any in-flight field-dropdown
	// edits on depth-2+ paths can flicker — selections occasionally degrade
	// to DanglingRefRow before the next render restores them. Memoizing
	// keeps option identity stable across the autosave debounce window.
	const addressableFields = useMemo(() => flattenFields(fields), [fields]);

	const errorsByRule = useRuleValidation(rules, fields, onValidationChange, isBuildex);

	const addRule = () => {
		onRulesChange([
			...rules,
			{
				id: newRuleId(),
				negated: false,
				ifConditions: [{ field: '', operator: 'const', value: '', required: true }],
				thenFields: [],
				elseFields: [],
			},
		]);
	};

	const updateRule = (ruleId, next) => {
		onRulesChange(rules.map((r) => (r.id === ruleId ? next : r)));
	};

	const deleteRule = (ruleId) => {
		onRulesChange(rules.filter((r) => r.id !== ruleId));
	};

	const duplicateRule = (ruleId) => {
		const original = rules.find((r) => r.id === ruleId);
		if (!original) return;
		const copy = JSON.parse(JSON.stringify(original));
		copy.id = newRuleId();
		const idx = rules.findIndex((r) => r.id === ruleId);
		const next = rules.slice();
		next.splice(idx + 1, 0, copy);
		onRulesChange(next);
	};

	return (
		<div className="dsv-xallof-builder" data-testid="xallof-builder">
			<div className="dsv-xallof-builder__header">
				<div>
					<h4 className="dsv-xallof-builder__title">
						Conditional Validation Rules
					</h4>
					<p className="dsv-xallof-builder__subtitle">
						Add if/then/else rules that apply field constraints only when
						specific IF conditions are met. Stored as
						{' '}
						<code>x-allOf</code>.
					</p>
				</div>
				<button
					type="button"
					className="dsv-xallof-builder__add-rule"
					onClick={addRule}
					data-testid="xallof-add-rule"
				>
					+ Add Rule
				</button>
			</div>

			{rules.length === 0 && (
				<div
					className="dsv-xallof-builder__empty"
					data-testid="xallof-empty-state"
				>
					<p className="dsv-xallof-builder__empty-title">
						No conditional rules yet
					</p>
					<p className="dsv-xallof-builder__empty-hint">
						Use rules to require fields only when others have specific values,
						validate numeric ranges, or enforce multi-field dependencies.
					</p>
				</div>
			)}

			{rules.map((rule, index) => (
				<RuleCard
					key={rule.id}
					rule={rule}
					ruleIndex={index}
					availableFields={addressableFields}
					errors={errorsByRule[rule.id]}
					onChange={(next) => updateRule(rule.id, next)}
					onDelete={() => deleteRule(rule.id)}
					onDuplicate={() => duplicateRule(rule.id)}
				/>
			))}
		</div>
	);
}

XAllOfBuilder.propTypes = {
	fields: PropTypes.array.isRequired,
	rules: PropTypes.array.isRequired,
	onRulesChange: PropTypes.func.isRequired,
	onValidationChange: PropTypes.func,
	isBuildex: PropTypes.bool,
};
